import { gql } from '@apollo/client';

/**
 * Ops incidents — the tickets the log monitor raises from production errors,
 * and any raised by hand.
 *
 * A ticket groups every occurrence of one error signature (`fingerprint`): how
 * often it happened, whom it touched, what the classifier thinks caused it, and
 * where the fix stands.
 */

/* What the list shows of each ticket. */
export const OPS_INCIDENT_SUMMARY_FIELDS = `
  id
  title
  severity
  priority
  priorityScore
  category
  status
  service
  occurrences
  usersImpactedCount
  usersImpacted
  likelyCause
  suggestedFix
  assignedTo
  firstSeenAt
  lastSeenAt
  reopenedCount
  classifiedBy
  fixHolding
`;

/* Everything about one ticket, for its detail view. */
export const OPS_INCIDENT_DETAIL_FIELDS = `
  id
  fingerprint
  title
  severity
  priority
  priorityScore
  category
  status
  service
  services
  loggerName
  level
  exceptionType
  messageTemplate
  sampleMessage
  tracebackTail
  firstSeenAt
  lastSeenAt
  occurrences
  lastWindowOccurrences
  ratePerMin
  consecutiveWindows
  escalationLevel
  escalationReason
  usersImpactedCount
  usersImpacted
  sessionsImpactedCount
  instances
  classifiedBy
  likelyCause
  suggestedFix
  rulesReason
  assignedTo
  resolutionNotes
  resolvedAt
  reopenedCount
  fixHolding
`;

/**
 * The tickets, narrowed. `status: ""` lists every status (the API's own
 * default is "open"); severity, priority and service are optional filters.
 */
export const OPS_INCIDENTS = gql`
  query OpsIncidents(
    $status: String
    $severity: String
    $priority: String
    $service: String
    $limit: Int!
  ) {
    opsIncidents(status: $status, severity: $severity, priority: $priority, service: $service, limit: $limit) {
      ${OPS_INCIDENT_SUMMARY_FIELDS}
    }
  }
`;

/** One ticket, whole. */
export const OPS_INCIDENT = gql`
  query OpsIncident($id: Int!) {
    opsIncident(id: $id) {
      ${OPS_INCIDENT_DETAIL_FIELDS}
    }
  }
`;

/**
 * The stored error lines behind the tickets, newest first, a page at a time.
 * Every argument is optional. `from`/`to` take a date ("2026-11-03", the whole
 * day) or a full time (`to` then exclusive); `fingerprint` is a ticket's, for
 * every line behind it. `cursor` is the previous page's `nextCursor`, left out
 * for the first page; `limit` is 1–1000 rows (default 100).
 */
export const GET_OPS_ERROR_LOGS = gql`
  query GetOpsErrorLogs(
    $from: String
    $to: String
    $service: String
    $logGroup: String
    $exceptionType: String
    $fingerprint: String
    $loggerName: String
    $level: String
    $search: String
    $cursor: String
    $limit: Int
  ) {
    getOpsErrorLogs(
      from: $from, to: $to, service: $service, logGroup: $logGroup,
      exceptionType: $exceptionType, fingerprint: $fingerprint, loggerName: $loggerName,
      level: $level, search: $search, cursor: $cursor, limit: $limit
    ) {
      items {
        id
        errorRaisedAt
        ingestedAt
        cloudwatchEventId
        logGroup
        service
        stream
        level
        loggerName
        exceptionType
        fingerprint
        message
        cascadeFollowups
        lastCascadeAt
        createdAt
        updatedAt
      }
      nextCursor
    }
  }
`;
